import type { ApprovalState } from '../approvals.js';
import { ALLOWED_FILE_TYPES, assertFileName, fileKindOf } from '../documents/file-types.js';
import type { DocumentKind } from '../documents/library.js';
import { recordAsOf } from '../documents/revision-state.js';
import { DomainError } from '../errors.js';
import { parseLocalDate } from '../local-date.js';
import { type RevisionTimeline, revisionStateAt, wasEverEffective } from './timeline.js';

/**
 * ISM form kaydı kuralları (ADR 0042 §1–§6, §13, §14; mimari belge §7.4). Kayıt doldurulduğu form
 * revizyonuna bağlıdır; taslak `form_records` satırındadır, ilk gönderim revizyon 1'i yazar ve
 * sonraki her değişiklik değiştirilemez yeni bir kayıt revizyonudur. Durum saklanmaz; değiştirilemez
 * kayıtlardan türetilir. Bu modül saftır: saat ve gemi saati farkı parametreyle gelir.
 */

/** Revizyonun kaynağı: şirket belgesi ya da geminin kendi belgesi (ADR 0036, ADR 0038 §1). */
export const REVISION_SOURCES = ['company', 'vessel'] as const;
export type RevisionSource = (typeof REVISION_SOURCES)[number];

/** Kullanılan şablon dosyası: revizyonun kaynak dosyası ya da baskı PDF'i (ADR 0028 #34(6)). */
export const TEMPLATE_ROLES = ['source', 'print_rendition'] as const;
export type TemplateRole = (typeof TEMPLATE_ROLES)[number];

/** Doldurma yolu (gereksinim §4; ADR 0042 §3): Office, yazdır-doldur-tara, uygulamada PDF. */
export const FILL_METHODS = ['office', 'print_scan', 'pdf_form'] as const;
export type FillMethod = (typeof FILL_METHODS)[number];

/**
 * Kayıt dosyasının kaynağı (ADR 0042 §1). `office_pdf`: Felixmate Office Pack'in Office dosyasından
 * ürettiği PDF kopyası (ADR 0070 §6); Office dosyası gibi taslakta tektir ve onunla birlikte değişir.
 */
export const RECORD_FILE_SOURCES = ['office', 'scan', 'photo', 'pdf_form', 'office_pdf'] as const;
export type RecordFileSource = (typeof RECORD_FILE_SOURCES)[number];

/**
 * Taslağa Office dosyası (ve PDF kopyası) koyar (ADR 0042 Ek U50; ADR 0070 §6): önceki Office
 * dosyası ve PDF kopyası çıkar, diğerleri sırasını korur; yenisi sona eklenir ve sıra yeniden
 * numaralanır. PDF verilmezse eski PDF kopyası da çıkar: başka bir dosyanın kopyası kayıtta kalmaz.
 */
export function withOfficeFiles<T extends { sortOrder: number; source: RecordFileSource }>(
  files: readonly T[],
  office: Omit<T, 'sortOrder' | 'source'>,
  pdf: Omit<T, 'sortOrder' | 'source'> | null,
): T[] {
  const kept = [...files]
    .filter((f) => f.source !== 'office' && f.source !== 'office_pdf')
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const added = [
    { ...office, source: 'office' },
    ...(pdf ? [{ ...pdf, source: 'office_pdf' }] : []),
  ] as T[];
  return [...kept, ...added].map((f, sortOrder) => ({ ...f, sortOrder }));
}

/** Dosya sınırları (ADR 0042 §6) [sorular #231]. */
export const MAX_RECORD_FILES = 20;
export const MAX_RECORD_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_RECORD_REVISION_BYTES = 200 * 1024 * 1024;

/** Gerekçe uzunluğu (ADR 0042 §13) [sorular #238]. */
export const MIN_RECORD_REASON_LENGTH = 5;
export const MAX_RECORD_REASON_LENGTH = 2000;

/**
 * Tarama görüntüsü (ADR 0042 §6; ADR 0029 K15'in 2048 px sınırı uygulanmaz): uzun kenarı A4'te
 * 300 dpi'nin (3508 px) altındaki görüntü uyarı alır, 4961 px'i (yaklaşık 420 dpi) aşan küçültülür.
 */
export const SCAN_MIN_LONG_EDGE_PX = 3508;
export const SCAN_MAX_LONG_EDGE_PX = 4961;

const SHA256 = /^[0-9a-f]{64}$/;

function oneOf<T extends string>(list: readonly T[], value: string, field: string): void {
  if (!(list as readonly string[]).includes(value)) {
    throw new DomainError('VALIDATION_FAILED', `${field} ${value}`, field);
  }
}

// ---------------------------------------------------------------------------------------------
// Gerekçe

/**
 * Kayıt gerekçesi (değişiklik, kâğıt istisnası, vazgeçme, değişmeden yeniden gönderim, iptal):
 * kırpılmış, en az 5 (`REASON_REQUIRED`), en çok 2000 karakter (`REASON_TOO_LONG`) [ADR 0042 §13].
 */
export function assertRecordReason(reason: string | null | undefined): string {
  const value = reason?.trim() ?? '';
  if (value.length < MIN_RECORD_REASON_LENGTH) throw new DomainError('REASON_REQUIRED');
  if (value.length > MAX_RECORD_REASON_LENGTH) throw new DomainError('REASON_TOO_LONG');
  return value;
}

/**
 * Kayıt revizyonunun gerekçesi (mimari belge §7.4): revizyon 1 ilk gönderimdir, gerekçe taşımaz;
 * sonraki her revizyon gönderimden sonraki değişikliktir, gerekçe zorunludur [MEPC.312 4.3.4].
 */
export function revisionReason(
  revisionNo: number,
  reason: string | null | undefined,
): string | null {
  if (!Number.isInteger(revisionNo) || revisionNo < 1) {
    throw new DomainError('VALIDATION_FAILED', `revision_no ${revisionNo}`, 'revision_no');
  }
  if (revisionNo === 1) {
    if ((reason?.trim() ?? '') !== '') {
      throw new DomainError('VALIDATION_FAILED', 'revision 1 reason', 'reason');
    }
    return null;
  }
  return assertRecordReason(reason);
}

// ---------------------------------------------------------------------------------------------
// İçerik

export interface RecordFileInput {
  sortOrder: number;
  name: string;
  sha256: string;
  size: number;
  mimeType: string;
}

export interface RecordRevisionContentInput {
  recordId: string;
  revisionNo: number;
  revisionSource: RevisionSource;
  revisionId: string;
  /** Bağlanan revizyonun `content_hash`'i. */
  templateContentHash: string;
  /** Kullanılan şablon dosyasının SHA-256'sı (ADR 0028 #34(6)). */
  templateSha256: string;
  templateRole: TemplateRole;
  fillMethod: FillMethod;
  paperRevisionReason: string | null;
  recordDay: string;
  title: string | null;
  note: string | null;
  files: readonly RecordFileInput[];
}

/**
 * Kayıt revizyonunun içerik nesnesi (ADR 0042 §2) [sorular #227]: `content_hash` =
 * SHA-256(JCS(içerik)); hash'i `packages/db` hesaplar. Revizyon no, revizyon bağı, şablon hash'leri
 * ve rolü, kâğıt istisnasının gerekçesi ve dosyalar onaylanan beyanın parçasıdır. Dosyalar
 * `sort_order`'a göre dizilir; giriş sırası içeriği değiştirmez. Onay bu hash'e bağlıdır
 * [MEPC.312 4.3.9].
 */
export function recordRevisionContent(input: RecordRevisionContentInput) {
  oneOf(REVISION_SOURCES, input.revisionSource, 'revision_source');
  oneOf(TEMPLATE_ROLES, input.templateRole, 'template_role');
  oneOf(FILL_METHODS, input.fillMethod, 'fill_method');
  if (!Number.isInteger(input.revisionNo) || input.revisionNo < 1) {
    throw new DomainError('VALIDATION_FAILED', `revision_no ${input.revisionNo}`, 'revision_no');
  }
  const files = [...input.files].sort(
    (a, b) =>
      a.sortOrder - b.sortOrder ||
      (a.sha256 < b.sha256 ? -1 : a.sha256 > b.sha256 ? 1 : 0) ||
      (a.name < b.name ? -1 : a.name > b.name ? 1 : 0),
  );
  return {
    recordId: input.recordId,
    revisionNo: input.revisionNo,
    revisionSource: input.revisionSource,
    revisionId: input.revisionId,
    templateContentHash: input.templateContentHash,
    templateSha256: input.templateSha256,
    templateRole: input.templateRole,
    fillMethod: input.fillMethod,
    paperRevisionReason: input.paperRevisionReason ?? null,
    recordDay: input.recordDay,
    title: input.title ?? null,
    note: input.note ?? null,
    files: files.map((f) => ({
      sortOrder: f.sortOrder,
      sha256: f.sha256,
      size: f.size,
      name: f.name,
      mimeType: f.mimeType,
    })),
  };
}
export type RecordRevisionContent = ReturnType<typeof recordRevisionContent>;

/**
 * Değişiklik bir şey değiştirmeli (`NO_CHANGE`) [sorular #238]: revizyon no dışında içerik
 * öncekiyle aynıysa yeni revizyon yazılmaz. İade edilen kaydı değişmeden yeniden göndermek ayrı
 * yoldur (gerekçeli yeniden gönderim; ADR 0042 §13).
 */
export function assertRecordChanged(
  previous: RecordRevisionContent,
  next: RecordRevisionContent,
): void {
  const { revisionNo: _previousNo, ...before } = previous;
  const { revisionNo: _nextNo, ...after } = next;
  if (JSON.stringify(before) === JSON.stringify(after)) throw new DomainError('NO_CHANGE');
}

// ---------------------------------------------------------------------------------------------
// Dosyalar

/**
 * Kayıt dosyaları (ADR 0042 §6) [sorular #231]:
 * - en az bir dosya (`NO_FILES`; taslakta `allowEmpty`), en çok 20 (`TOO_MANY_FILES`);
 * - dosya başına 50 MiB, revizyonda toplam 200 MiB (`FILE_TOO_LARGE`, ayrıntı `file` / `total`);
 *   boş dosya `EMPTY_FILE`;
 * - aynı SHA-256 iki kez yok (`DUPLICATE_FILE`); sıra numaraları tekil;
 * - türler DOCX, XLSX, PDF, JPG, PNG (gereksinim §4); makrolu ve diğer türler
 *   `UNSUPPORTED_FILE_TYPE` [ADR 0028 #35]; bildirilen tür uzantıyla uyuşmalı
 *   (`FILE_TYPE_MISMATCH`). İçerik imzası ve makro denetimi baytları okuyan sunucunun işidir.
 */
export function assertRecordFiles(
  files: readonly RecordFileInput[],
  options: { allowEmpty?: boolean } = {},
): void {
  if (files.length === 0 && !options.allowEmpty) throw new DomainError('NO_FILES');
  if (files.length > MAX_RECORD_FILES) {
    throw new DomainError('TOO_MANY_FILES', String(files.length));
  }
  const hashes = new Set<string>();
  const orders = new Set<number>();
  let total = 0;
  for (const f of files) {
    assertFileName(f.name);
    const kind = fileKindOf(f.name);
    if (f.mimeType !== ALLOWED_FILE_TYPES[kind].mimeType) {
      throw new DomainError('FILE_TYPE_MISMATCH', `${f.name} ${f.mimeType}`, kind);
    }
    if (!SHA256.test(f.sha256)) throw new DomainError('VALIDATION_FAILED', f.sha256, 'sha256');
    if (!Number.isInteger(f.sortOrder) || f.sortOrder < 0 || orders.has(f.sortOrder)) {
      throw new DomainError('VALIDATION_FAILED', `sort_order ${f.sortOrder}`, 'sort_order');
    }
    orders.add(f.sortOrder);
    if (!Number.isInteger(f.size)) throw new DomainError('VALIDATION_FAILED', f.name, 'size');
    if (f.size <= 0) throw new DomainError('EMPTY_FILE', f.name);
    if (f.size > MAX_RECORD_FILE_BYTES) {
      throw new DomainError('FILE_TOO_LARGE', `${f.name} ${f.size}`, 'file');
    }
    if (hashes.has(f.sha256)) throw new DomainError('DUPLICATE_FILE', f.sha256);
    hashes.add(f.sha256);
    total += f.size;
  }
  if (total > MAX_RECORD_REVISION_BYTES) {
    throw new DomainError('FILE_TOO_LARGE', `total ${total}`, 'total');
  }
}

export type ScanImageWarning = 'LOW_RESOLUTION';

export interface ScanImageCheck {
  warnings: ScanImageWarning[];
  /** Küçültülecekse yeni boyut (uzun kenar 4961 px, oran korunur); yoksa `null`. */
  resizeTo: { width: number; height: number } | null;
}

/**
 * Tarama ya da fotoğraf görüntüsü (ADR 0042 §6) [sorular #231]: uzun kenar 3508 px'in altındaysa
 * `LOW_RESOLUTION` uyarısı (ret yok; arşiv tarama rehberleri en az 300 dpi ister); 4961 px'i aşarsa
 * küçültülür. Küçültme hash'ten önce yapılır; saklanan ve onaylanan bayt küçültülmüş dosyadır.
 */
export function scanImageCheck(size: { width: number; height: number }): ScanImageCheck {
  const { width, height } = size;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
    throw new DomainError('VALIDATION_FAILED', `image ${width}x${height}`, 'image_size');
  }
  const long = Math.max(width, height);
  const warnings: ScanImageWarning[] = long < SCAN_MIN_LONG_EDGE_PX ? ['LOW_RESOLUTION'] : [];
  if (long <= SCAN_MAX_LONG_EDGE_PX) return { warnings, resizeTo: null };
  const scale = SCAN_MAX_LONG_EDGE_PX / long;
  const scaled = (edge: number) =>
    edge === long ? SCAN_MAX_LONG_EDGE_PX : Math.max(1, Math.round(edge * scale));
  return { warnings, resizeTo: { width: scaled(width), height: scaled(height) } };
}

/** JPEG'de boyut taşıyan kare başı işaretleri (SOF0–SOF15; DHT, JPG ve DAC hariç). */
const JPEG_SOF = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

/**
 * PNG (IHDR) ya da JPEG (SOF) başlığından görüntü boyutu; tarama uyarısı için (ADR 0042 §6).
 * Başlık tanınmaz ya da kesikse `null`: uyarı verilmez, dosya reddedilmez.
 */
export function imageDimensions(head: Uint8Array): { width: number; height: number } | null {
  const view = new DataView(head.buffer, head.byteOffset, head.byteLength);
  const isPng =
    head.length >= 24 &&
    head[0] === 0x89 &&
    head[1] === 0x50 &&
    head[12] === 0x49 &&
    head[13] === 0x48 &&
    head[14] === 0x44 &&
    head[15] === 0x52;
  if (isPng) {
    const width = view.getUint32(16);
    const height = view.getUint32(20);
    return width > 0 && height > 0 ? { width, height } : null;
  }
  if (head.length < 4 || head[0] !== 0xff || head[1] !== 0xd8) return null;
  let i = 2;
  while (i + 3 < head.length) {
    if (head[i] !== 0xff) return null;
    const marker = head[i + 1] as number;
    if (marker === 0xff) {
      i += 1;
      continue;
    }
    // Uzunluksuz işaretler
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
      i += 2;
      continue;
    }
    if (marker === 0xda) return null;
    const length = view.getUint16(i + 2);
    if (JPEG_SOF.has(marker)) {
      if (i + 8 >= head.length) return null;
      const height = view.getUint16(i + 5);
      const width = view.getUint16(i + 7);
      return width > 0 && height > 0 ? { width, height } : null;
    }
    i += 2 + length;
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Kayıt numarası

export interface RecordNumber {
  recordYear: number;
  recordSeq: number;
  recordNo: string;
}

/**
 * Kayıt no `FR-<YYYY>-<NNNNN>` (ADR 0042 §5) [sorular #230]: gemi başına, kayıt gününün (gemi
 * yerel günü) yılı başına, 1'den boşluksuz sıra. `lastSeq` o yılın son sırasıdır (yoksa 0).
 * Vazgeçilen taslağın numarası boşa çıkmaz; sıra yine boşluksuzdur. Gemi sunucusu verir.
 */
export function nextRecordNumber(recordDay: string, lastSeq: number): RecordNumber {
  const { year } = parseLocalDate(recordDay);
  if (!Number.isInteger(lastSeq) || lastSeq < 0) {
    throw new DomainError('VALIDATION_FAILED', `last_seq ${lastSeq}`, 'record_seq');
  }
  const recordSeq = lastSeq + 1;
  return {
    recordYear: year,
    recordSeq,
    recordNo: `FR-${String(year).padStart(4, '0')}-${String(recordSeq).padStart(5, '0')}`,
  };
}

// ---------------------------------------------------------------------------------------------
// Revizyon bağı

export type PaperFlag = 'paper_on_superseded' | 'paper_on_withdrawn';

export interface NewRecordCheck {
  documentKind: DocumentKind;
  fillMethod: FillMethod;
  revisionId: string;
  /** Revizyonun belge dalı (şirket ya da gemi kütüphanesi). */
  timeline: RevisionTimeline;
  /** Taslağın açıldığı an (gemi sunucusu saati), UTC. */
  at: string;
  /** Gemi saati farkı, dakika. */
  tzOffsetMin: number;
  /** Kâğıt istisnasının gerekçesi; yalnız yürürlükte olmayan revizyonda. */
  paperRevisionReason?: string | null;
}

export interface NewRecordBinding {
  /** Kayıt günü: açılış anının gemi yerel günü; kullanıcı değiştiremez (ADR 0042 §5). */
  recordDay: string;
  /** Kâğıt istisnası işareti; olağan kayıtta `null`. */
  paper: PaperFlag | null;
  paperRevisionReason: string | null;
}

/** Bir zamanlar yürürlükte olabilmiş durumlar: kâğıt istisnasının adayları. */
const PAPER_STATES = {
  superseded: 'paper_on_superseded',
  withdrawn: 'paper_on_withdrawn',
} as const;

/**
 * Yeni taslağın revizyon bağı (ADR 0042 §3) [sorular #228]:
 * - Kayıt yalnız form belgesine açılır (`NOT_A_FORM`).
 * - Revizyon açılış anının gemi yerel gününde yürürlükte olmalı (`REVISION_NOT_CURRENT`)
 *   [ISM 11.2.3; ADR 0027 §13].
 * - Kâğıt istisnası: yalnız `print_scan` yolunda, aynı belgenin bir zamanlar yürürlüğe girmiş
 *   (sonradan kalkmış ya da geri çekilmiş) revizyonu; taslak, zamanlanmış ya da yürürlüğe girmeden
 *   geri çekilmiş revizyon `REVISION_NOT_PUBLISHED` (gemide hiç basılamamıştır; ADR 0028 #30 B);
 *   gerekçe zorunlu. Kayıt işaretlenir (`paper_on_superseded`, `paper_on_withdrawn`).
 * Belgenin o gemide görünürlüğü ve gemi belgesinin gemisi (`VESSEL_MISMATCH`) doğrulayıcıdadır.
 */
export function assertRevisionForNewRecord(check: NewRecordCheck): NewRecordBinding {
  if (check.documentKind !== 'form') throw new DomainError('NOT_A_FORM', check.documentKind);
  oneOf(FILL_METHODS, check.fillMethod, 'fill_method');
  const recordDay = recordAsOf(check.at, check.tzOffsetMin);
  // Açılış anında gemide bilinen her olay olmuştur: olayın zamanı bulutun saatiyle yazılır, gemi
  // saati gerideyse olay geleceğe düşmesin (saat kayması kayıt kaybettirmez; ADR 0042 Ek U22)
  const opened = Date.parse(check.at);
  const timeline = check.timeline.map((revision) => ({
    ...revision,
    events: revision.events.map((event) =>
      Date.parse(event.at) > opened ? { ...event, at: check.at } : event,
    ),
  }));
  const state = revisionStateAt(timeline, check.revisionId, check.at, check.tzOffsetMin);
  const given = check.paperRevisionReason?.trim() ?? '';
  if (state === 'published') {
    // Yürürlükteki revizyonla olağan kayıt: kâğıt gerekçesi yoktur
    if (given !== '') {
      throw new DomainError(
        'VALIDATION_FAILED',
        'paper reason on current revision',
        'paper_revision_reason',
      );
    }
    return { recordDay, paper: null, paperRevisionReason: null };
  }
  if (check.fillMethod !== 'print_scan') throw new DomainError('REVISION_NOT_CURRENT', state);
  const paper = state === 'superseded' || state === 'withdrawn' ? PAPER_STATES[state] : null;
  if (
    paper === null ||
    !wasEverEffective(timeline, check.revisionId, check.at, check.tzOffsetMin)
  ) {
    throw new DomainError('REVISION_NOT_PUBLISHED', state);
  }
  return { recordDay, paper, paperRevisionReason: assertRecordReason(given) };
}

// ---------------------------------------------------------------------------------------------
// Durum

export const RECORD_STATUSES = [
  'draft',
  'discarded',
  'pending',
  'returned',
  'approved',
  'void',
  // Okuma modelinde tutarsız kayıt (ADR 0042 Ek U23): ör. bulut gönderimi karantinaya alıp
  // revizyonu uyguladı. Kayıt kaybolmaz ve görünür; hiçbir işlem verilmez
  'inconsistent',
] as const;
export type RecordStatus = (typeof RECORD_STATUSES)[number];

export interface RecordStatusInput {
  /** Son kayıt revizyonunun numarası; hiç gönderilmemiş taslakta `null`. */
  latestRevisionNo: number | null;
  /** `record_voids`'ta iptal kaydı var mı (ADR 0029 K14). */
  voided: boolean;
  /** Hiç gönderilmemiş taslaktan vazgeçildi mi (`deleted_at`; ADR 0042 §14). */
  discarded: boolean;
  /** Son gönderimin onay durumu (`deriveApprovalState`); taslakta `null`. */
  approval: ApprovalState | null;
  /** Son gönderimin kuralındaki adım sayısı. */
  stepCount: number;
}

export interface RecordStatusView {
  status: RecordStatus;
  revisionNo: number | null;
  /** Gönderimden sonra değişti mi: son revizyon no > 1 (mimari belge §7.4; ADR 0042 §20). */
  amended: boolean;
  /** Bekleyen adım ve adım sayısı ("adım n / m"). */
  step?: number;
  steps?: number;
  /** Bekleyen adım ofiste (ADR 0026 §3). */
  office?: true;
  /** `inconsistent` durumunda türetmenin hata kodu (ör. `INVALID_RECORD_STATE`). */
  error?: string;
}

/**
 * Kaydın durumu (mimari belge §7.2; ADR 0042 §13, §14): saklanmaz, revizyonlardan, gönderim ve
 * onaylardan, iptal ve vazgeçme kaydından türetilir. İptal her durumun önüne geçer. Revizyonu olan
 * kayıtta gönderim her zaman aynı transaction'da yazılır; gönderimsiz revizyon tutarsızlıktır
 * (`INVALID_RECORD_STATE`).
 */
export function deriveRecordStatus(input: RecordStatusInput): RecordStatusView {
  const revisionNo = input.latestRevisionNo;
  const base = { revisionNo, amended: revisionNo !== null && revisionNo > 1 };
  if (input.voided) return { status: 'void', ...base };
  if (revisionNo === null) return { status: input.discarded ? 'discarded' : 'draft', ...base };
  const approval = input.approval;
  switch (approval?.status) {
    case 'pending':
      return {
        status: 'pending',
        ...base,
        step: approval.step,
        steps: input.stepCount,
        ...(approval.office ? { office: true as const } : {}),
      };
    case 'returned':
      return { status: 'returned', ...base };
    case 'approved':
      return { status: 'approved', ...base };
    default:
      throw new DomainError('INVALID_RECORD_STATE', String(approval?.status ?? null));
  }
}

/**
 * Okuma modelinde durumu türetilemeyen kayıt (ADR 0042 Ek U23): bulut gönderimi (ya da onay
 * satırını) karantinaya alıp revizyonu uygulamış olabilir. Kayıt kayıt kaybettirmemek için
 * listede ve ayrıntıda "inconsistent" olarak görünür; yazma yolu yine `deriveRecordStatus`'un
 * reddini kullanır ve bu durumda hiçbir işlem verilmez.
 */
export function inconsistentRecordStatus(
  latestRevisionNo: number | null,
  error: string,
): RecordStatusView {
  return {
    status: 'inconsistent',
    revisionNo: latestRevisionNo,
    amended: latestRevisionNo !== null && latestRevisionNo > 1,
    error,
  };
}

// ---------------------------------------------------------------------------------------------
// İşlemler

/**
 * Kayıt üzerindeki işlemler (ADR 0042 §1, §12–§15): taslağı düzenleme, taslaktan vazgeçme, ilk
 * gönderim, değişiklik (yeni revizyon), değişmeden yeniden gönderim, onay kararı ve iptal.
 */
export const RECORD_ACTIONS = [
  'edit_draft',
  'discard',
  'submit',
  'amend',
  'resubmit',
  'decide',
  'void',
] as const;
export type RecordAction = (typeof RECORD_ACTIONS)[number];

const DRAFT_ACTIONS: readonly RecordAction[] = ['edit_draft', 'discard', 'submit'];

/**
 * İşlem kaydın durumunda izinli mi:
 * - İptal edilmiş kayıtta hiçbir işlem (`RECORD_VOID`; yeniden iptal `ALREADY_VOIDED`) [sorular #239].
 * - Vazgeçilmiş taslakta hiçbir işlem (`RECORD_DISCARDED`).
 * - Taslakta düzenleme, vazgeçme ve gönderim serbest; gönderilmiş kayda özgü işlemler
 *   `RECORD_NOT_SUBMITTED`.
 * - Gönderilmiş kayıtta taslak alanları ve revizyon bağı kilitlidir (`RECORD_SUBMITTED`)
 *   [sorular #226]; değişiklik ve iptal her durumda, değişmeden yeniden gönderim yalnız iadede
 *   (`NOT_RETURNED`), karar yalnız bekleyen kayıtta (`NOT_PENDING`).
 * Yetki (matris) ve parola ayrıca denetlenir (ADR 0042 §11, §12).
 */
export function assertRecordAction(view: RecordStatusView, action: RecordAction): void {
  oneOf(RECORD_ACTIONS, action, 'action');
  switch (view.status) {
    case 'inconsistent':
      throw new DomainError('INVALID_RECORD_STATE', view.error ?? action);
    case 'void':
      throw new DomainError(action === 'void' ? 'ALREADY_VOIDED' : 'RECORD_VOID', action);
    case 'discarded':
      throw new DomainError('RECORD_DISCARDED', action);
    case 'draft':
      if (!DRAFT_ACTIONS.includes(action)) throw new DomainError('RECORD_NOT_SUBMITTED', action);
      return;
    default:
      if (DRAFT_ACTIONS.includes(action)) throw new DomainError('RECORD_SUBMITTED', action);
      if (action === 'resubmit' && view.status !== 'returned') {
        throw new DomainError('NOT_RETURNED', view.status);
      }
      if (action === 'decide' && view.status !== 'pending') {
        throw new DomainError('NOT_PENDING', view.status);
      }
  }
}
